import React, { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  TextField,
  createFilterOptions,
  Switch,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { CustomAxios as axios } from '../../common/axios';
import type {
  LineMaster,
  StationMaster,
  StationWithLine,
} from '../../types/station/station';
import type { SelectLineStationType } from '../pages/Home';

type SelectStationProps = {
  selectedList: SelectLineStationType[];
  selectedStation?: StationWithLine;
  setSelectedStation: (station: StationWithLine) => void;
  line?: LineMaster;
  index: number;
  readOnly?: boolean;
};

export const SelectStation = ({
  selectedList,
  selectedStation,
  setSelectedStation,
  line,
  index,
  readOnly = false,
}: SelectStationProps) => {
  const [stations, setStations] = useState<StationWithLine[]>([]);
  const [isArrival, setIsArrival] = useState(false);

  const fetchStations = useCallback(
    async (inputText?: string) => {
      // 最初の選択駅の場合
      if (line === undefined) {
        if (inputText?.trim() === '') {
          setStations([]);
          return;
        }
        try {
          const response = await axios.post(`/station/name`, {
            stationName: inputText,
          });
          setStations(response.data);
        } catch (err) {
          console.warn(err);
        }
      }
      // 路線選択後の選択駅を取得
      else {
        try {
          const response = await axios.post(`/station/line`, {
            lineCode: line.lineCode,
          });
          setStations(response.data);
        } catch (err) {
          console.warn(err);
        }
      }
    },
    [line]
  );

  useEffect(() => {
    if (line) {
      fetchStations();
    }
  }, [line]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    fetchStations(event.target.value);
  };

  const isDispTarget = (station: StationWithLine, i: number) => {
    if (selectedList[i - 1]?.station?.stationCode === station?.stationCode) {
      return false;
    }
    if (
      !isArrival &&
      !(station?.lines ?? []).filter(
        (item) => item?.lineCode !== selectedList[i - 1].line?.lineCode
      ).length
    ) {
      return false;
    }
    return true;
  };

  return (
    <div className="flex items-center gap-4">
      <Autocomplete
        options={stations}
        getOptionLabel={(station) => station.stationName}
        value={selectedStation}
        onChange={(_, value) => setSelectedStation(value!)}
        onBlur={() => setStations([])}
        onFocus={() => fetchStations()}
        sx={{ width: 350 }}
        filterOptions={
          index
            ? (options) => options.filter((item) => isDispTarget(item, index))
            : (options) => options
        }
        readOnly={readOnly}
        renderInput={(params) => (
          <TextField
            {...params}
            label={!index ? '発駅' : '経由駅'}
            placeholder="東京"
            onChange={handleInputChange}
          />
        )}
      />
      {index > 0 &&
        selectedList.length === index + 1 &&
        !selectedList[selectedList.length - 1]?.line && (
          <FormControlLabel
            control={<Switch onChange={(_, value) => setIsArrival(value)} />}
            label="着駅"
          />
        )}
    </div>
  );
};
